import { z } from "zod";

/*
 * Protokoll-Vorlagen: Formulare für Prüfprotokolle, Unterweisungen, Gefährdungsbeurteilungen, Abnahmen
 * und Wartungen. Ausgefüllt am PC oder Handy, unterschrieben auf dem Bildschirm, abgeschlossen als PDF im
 * Projektordner. Normen werden nur benannt, ihr Text ist urheberrechtlich geschützt und steht nicht hier.
 */

export type ProtocolCategory = "pruefung" | "arbeitsschutz" | "abnahme";

export const PROTOCOL_CATEGORIES: Record<ProtocolCategory, string> = {
  pruefung: "Prüfprotokolle",
  arbeitsschutz: "Arbeitsschutz",
  abnahme: "Abnahme und Wartung",
};

/** Ergebnis eines Prüfpunkts: in Ordnung, nicht in Ordnung, entfällt. */
export type CheckValue = "ok" | "mangel" | "entfaellt";
export const CHECK_LABELS: Record<CheckValue, string> = { ok: "i. O.", mangel: "n. i. O.", entfaellt: "entfällt" };

/** Werte vorbelegen, damit niemand Firma, Kunde und Datum abtippt. */
export type Prefill = "company" | "user" | "today" | "customer" | "address" | "project";

/**
 * Grenzwert als Hinweis beim Ausfüllen. HandVero bewertet nicht selbst: Liegt ein Wert außerhalb, wird er nur
 * markiert. `label` steht im Formular und im PDF, `min`/`max` markieren eindeutig auffällige Werte.
 */
export interface Limit {
  label: string;
  min?: number;
  max?: number;
}

export type ColumnType = "text" | "number" | "check" | "choice" | "date" | "signature";

export interface ColumnDef {
  key: string;
  label: string;
  type: ColumnType;
  unit?: string;
  options?: string[];
  limit?: Limit;
  /** Relative Breite im PDF (Standard 1). */
  width?: number;
}

export type FieldType = "text" | "textarea" | "date" | "number" | "choice" | "multi" | "check" | "table" | "signature" | "photos" | "info";

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  hint?: string;
  unit?: string;
  options?: string[];
  limit?: Limit;
  required?: boolean;
  prefill?: Prefill;
  /** Startwert, z. B. die übliche Netzspannung. */
  initial?: string;
  /** Tabellen: Spalten und wie viele leere Zeilen zu Beginn da sind. */
  columns?: ColumnDef[];
  rows?: number;
  /** Unterschrift: Text, den die Person mit ihrer Unterschrift bestätigt. */
  confirm?: string;
}

export interface SectionDef {
  title: string;
  intro?: string;
  fields: FieldDef[];
}

export interface ProtocolTemplate {
  id: string;
  name: string;
  category: ProtocolCategory;
  description: string;
  /** Rechtsgrundlagen und Regeln, auf die sich die Vorlage bezieht (nur Fundstellen, keine Normtexte). */
  basis: string[];
  /** Stichwörter, an denen der passende Projektordner für das PDF erkannt wird. */
  folderWords: string[];
  sections: SectionDef[];
}

export interface Signature {
  /** PNG als data-URL. */
  image: string;
  name: string;
  signedAt: string;
}

export interface PhotoRef {
  fileId: string;
  caption: string;
}

export type ProtocolValue = string | string[] | Signature | PhotoRef[] | Record<string, unknown>[] | null;
export type ProtocolData = Record<string, ProtocolValue>;

export type ProtocolStatus = "draft" | "completed";

export interface ProtocolSummary {
  id: string;
  projectId: string;
  templateId: string;
  templateName: string;
  title: string;
  status: ProtocolStatus;
  createdBy: string | null;
  updatedAt: string;
  completedAt: string | null;
  fileId: string | null;
  folderId: string | null;
  /** Darf die Person diesen Entwurf löschen? */
  canDelete: boolean;
}

export interface ProtocolDetail extends ProtocolSummary {
  data: ProtocolData;
  contentHash: string | null;
}

export interface ProjectPhoto {
  id: string;
  name: string;
  folderName: string;
  hasThumb: boolean;
  createdAt: string;
}

/** Steht unter jedem Protokoll im PDF und in der App. */
export const PROTOCOL_DISCLAIMER =
  "HandVero stellt diese Vorlage als Arbeitshilfe bereit und erbringt keine Prüf-, Sachverständigen- oder Rechtsberatungsleistung. " +
  "Ob dieses Dokument im Einzelfall den gesetzlichen, normativen und vertraglichen Anforderungen genügt und rechtlich verwertbar ist, " +
  "muss der Verwender selbst prüfen. Normen und Regeln sind nur benannt; maßgeblich ist die jeweils gültige Fassung. " +
  "Für Inhalt, Messwerte und Bewertung ist allein der Unterzeichnende verantwortlich.";

export const PROTOCOL_DISCLAIMER_SHORT = "Vorlage von HandVero. Ob das Dokument im Einzelfall rechtlich genügt, prüft der Verwender selbst.";

export const SIGNATURE_NOTE =
  "Unterschriften wurden auf dem Bildschirm geleistet (einfache elektronische Signatur). Wo das Gesetz die Schriftform verlangt, " +
  "ersetzt diese Unterschrift sie nicht.";

/* ---------- Bausteine für die Vorlagen ---------- */

const f = {
  text: (key: string, label: string, more: Partial<FieldDef> = {}): FieldDef => ({ key, label, type: "text", ...more }),
  area: (key: string, label: string, more: Partial<FieldDef> = {}): FieldDef => ({ key, label, type: "textarea", ...more }),
  date: (key: string, label: string, more: Partial<FieldDef> = {}): FieldDef => ({ key, label, type: "date", ...more }),
  num: (key: string, label: string, unit: string, more: Partial<FieldDef> = {}): FieldDef => ({ key, label, type: "number", unit, ...more }),
  choice: (key: string, label: string, options: string[], more: Partial<FieldDef> = {}): FieldDef => ({ key, label, type: "choice", options, ...more }),
  multi: (key: string, label: string, options: string[], more: Partial<FieldDef> = {}): FieldDef => ({ key, label, type: "multi", options, ...more }),
  check: (key: string, label: string, hint?: string): FieldDef => ({ key, label, type: "check", hint }),
  table: (key: string, label: string, columns: ColumnDef[], rows = 3, more: Partial<FieldDef> = {}): FieldDef => ({ key, label, type: "table", columns, rows, ...more }),
  sign: (key: string, label: string, more: Partial<FieldDef> = {}): FieldDef => ({ key, label, type: "signature", ...more }),
  photos: (key = "fotos", label = "Fotos zum Protokoll"): FieldDef => ({
    key,
    label,
    type: "photos",
    hint: "Fotos aus dem Projekt wählen. Sie erscheinen mit Beschriftung am Ende des PDFs.",
  }),
  info: (key: string, label: string, hint: string): FieldDef => ({ key, label, type: "info", hint }),
};

const col = (key: string, label: string, type: ColumnType = "text", more: Partial<ColumnDef> = {}): ColumnDef => ({ key, label, type, ...more });

const MESSGERAETE = f.table(
  "messgeraete",
  "Verwendete Mess- und Prüfgeräte",
  [col("geraet", "Gerät / Typ", "text", { width: 2 }), col("seriennr", "Serien- / Inventar-Nr."), col("kalibriert", "Kalibriert bis", "date")],
  1,
  { hint: "Messgeräte nach DIN EN 61557 bzw. für die jeweilige Prüfung geeignet." },
);

const PRUEFER = [
  f.date("pruefdatum", "Datum der Prüfung", { prefill: "today", required: true }),
  f.text("pruefer", "Verantwortlicher Prüfer", { prefill: "user", required: true }),
  f.choice("qualifikation", "Qualifikation", ["Elektrofachkraft", "Zur Prüfung befähigte Person (BetrSichV)", "Elektrotechnisch unterwiesene Person unter Leitung einer Elektrofachkraft"]),
];

const MAENGEL = f.table(
  "maengel",
  "Festgestellte Mängel und Empfehlungen",
  [
    col("einstufung", "Einstufung", "choice", { options: ["Sofort beheben", "Zeitnah beheben", "Bei Prüfung behoben", "Empfehlung", "Information"], width: 1.2 }),
    col("beschreibung", "Mangel / Maßnahme", "text", { width: 3 }),
    col("ort", "Ort / Stromkreis", "text", { width: 1.2 }),
  ],
  1,
  { hint: "Für die Beseitigung von Mängeln ist der Betreiber der Anlage verantwortlich." },
);

const ERGEBNIS_ANLAGE = f.choice(
  "ergebnis",
  "Gesamtergebnis",
  [
    "Keine Mängel festgestellt. Die geforderten Schutzmaßnahmen sind wirksam.",
    "Mängel festgestellt, deren Beseitigung erforderlich ist. Die Schutzmaßnahmen sind wirksam.",
    "Erhebliche Mängel festgestellt. Die Schutzmaßnahmen sind nicht ausreichend wirksam; der Betrieb ist bis zur Beseitigung einzustellen oder einzuschränken.",
  ],
  { required: true },
);

const UNTERSCHRIFT_PRUEFER = f.sign("sig_pruefer", "Verantwortlicher Prüfer", {
  required: true,
  confirm: "Die Prüfung wurde nach bestem Wissen und den anerkannten Regeln der Technik durchgeführt.",
});
const UNTERSCHRIFT_BETREIBER = f.sign("sig_betreiber", "Auftraggeber / Betreiber", {
  confirm: "Ich habe das Protokoll erhalten. Mir ist bekannt, dass ich als Betreiber für die Beseitigung festgestellter Mängel verantwortlich bin.",
});

const NAECHSTE_PRUEFUNG = f.date("naechste", "Nächste Prüfung spätestens", {
  hint: "Die Frist legt der Betreiber in seiner Gefährdungsbeurteilung fest. Richtwert nach DGUV Vorschrift 3 für ortsfeste Anlagen: 4 Jahre, in Betriebsstätten besonderer Art 1 Jahr.",
});

/* ---------- Die Vorlagen ---------- */

export const PROTOCOL_TEMPLATES: ProtocolTemplate[] = [
  {
    id: "elektro-ortsfest",
    name: "Prüfprotokoll ortsfeste elektrische Anlage",
    category: "pruefung",
    description: "Erst- und Wiederholungsprüfung von Anlagen und Verteilungen: Besichtigen, Erproben, Messen je Stromkreis, Mängel und Ergebnis.",
    basis: [
      "DGUV Vorschrift 3, § 5 (Prüfungen)",
      "Betriebssicherheitsverordnung (BetrSichV), § 14",
      "DIN VDE 0100-600 (Erstprüfung)",
      "DIN VDE 0105-100 (Wiederkehrende Prüfung)",
      "DIN VDE 0100-410 (Schutz gegen elektrischen Schlag)",
      "DIN EN 61557 (Messgeräte)",
    ],
    folderWords: ["prüfprotokoll", "messungen", "prüfung"],
    sections: [
      {
        title: "Auftrag und Anlage",
        fields: [
          f.text("auftraggeber", "Auftraggeber / Betreiber", { prefill: "customer", required: true }),
          f.text("anlagenort", "Anschrift der Anlage", { prefill: "address", required: true }),
          f.text("anlage", "Geprüfte Anlage / Teilanlage", { required: true, hint: "z. B. Hauptverteilung und Unterverteilung Halle 2" }),
          f.choice("anlass", "Prüfanlass", ["Erstprüfung (Neuanlage)", "Wiederholungsprüfung", "Prüfung nach Änderung oder Erweiterung", "Prüfung nach Instandsetzung"], { required: true }),
          f.choice("netzform", "Netzform", ["TN-S", "TN-C-S", "TN-C", "TT", "IT"]),
          f.text("netzspannung", "Netzspannung", { initial: "3/N/PE 400/230 V, 50 Hz" }),
          ...PRUEFER,
        ],
      },
      { title: "Messgeräte", fields: [MESSGERAETE] },
      {
        title: "Besichtigen",
        intro: "Sichtprüfung vor dem Messen, bei Wiederholungsprüfungen im Umfang nach DIN VDE 0105-100.",
        fields: [
          f.check("b_auswahl", "Auswahl und Zustand der Betriebsmittel"),
          f.check("b_beruehrung", "Schutz gegen direktes Berühren (Abdeckungen, Gehäuse, Isolierung)"),
          f.check("b_leitungen", "Leitungen, Klemmen und Verbindungen ohne Beschädigung"),
          f.check("b_spa", "Schutzpotentialausgleich vorhanden und angeschlossen"),
          f.check("b_kennzeichnung", "Stromkreise und Schutzeinrichtungen gekennzeichnet"),
          f.check("b_brandschutz", "Brandschutz, Abschottungen und Abstände"),
          f.check("b_unterlagen", "Schaltpläne und Unterlagen vorhanden"),
          f.check("b_zugang", "Zugänglichkeit und Bedienbarkeit"),
        ],
      },
      {
        title: "Erproben",
        fields: [
          f.check("e_rcd", "Fehlerstrom-Schutzeinrichtungen über Prüftaste ausgelöst"),
          f.check("e_schalter", "Schalt-, Steuer- und Verriegelungseinrichtungen"),
          f.check("e_notaus", "Not-Aus / Not-Halt"),
          f.check("e_drehfeld", "Drehfeld der Drehstromsteckdosen rechts"),
          f.check("e_iso", "Isolationsüberwachung (nur IT-System)"),
        ],
      },
      {
        title: "Messen",
        fields: [
          f.choice("messbedingung", "Messbedingungen", ["trocken", "feucht", "nass"]),
          f.num("r_spa", "Durchgängigkeit Schutzpotentialausgleich", "Ω"),
          f.num("r_a", "Widerstand des Anlagenerders (TT)", "Ω"),
          f.table(
            "stromkreise",
            "Messwerte je Stromkreis",
            [
              col("nr", "Nr.", "text", { width: 0.6 }),
              col("ort", "Ort / Anlagenteil", "text", { width: 2 }),
              col("leitung", "Leitung", "text", { width: 1.2 }),
              col("schutz", "Sicherung", "text", { width: 1 }),
              col("rpe", "R PE", "number", { unit: "Ω" }),
              col("riso", "R Iso", "number", { unit: "MΩ", limit: { label: "≥ 1 MΩ", min: 1 } }),
              col("zs", "Zs", "number", { unit: "Ω" }),
              col("ik", "Ik", "number", { unit: "A" }),
              col("idn", "IΔn", "number", { unit: "mA" }),
              col("ta", "ta", "number", { unit: "ms" }),
              col("ok", "Ergebnis", "check", { width: 0.9 }),
            ],
            3,
            {
              hint: "Zs ist mit dem Höchstwert für die Abschaltbedingung des Schutzorgans zu vergleichen (DIN VDE 0100-410). Isolationswiderstand bei 500 V DC mindestens 1 MΩ. Empfindliche Verbraucher vor der Isolationsmessung trennen.",
            },
          ),
        ],
      },
      { title: "Mängel", fields: [MAENGEL] },
      { title: "Ergebnis", fields: [ERGEBNIS_ANLAGE, NAECHSTE_PRUEFUNG, f.area("bemerkung", "Bemerkungen"), f.photos()] },
      { title: "Unterschriften", fields: [UNTERSCHRIFT_PRUEFER, UNTERSCHRIFT_BETREIBER] },
    ],
  },
  {
    id: "elektro-geraete",
    name: "Prüfprotokoll ortsveränderliche Geräte",
    category: "pruefung",
    description: "Wiederholungsprüfung von Geräten, Verlängerungen und Werkzeugen mit Messwerten je Gerät.",
    basis: [
      "DGUV Vorschrift 3, § 5 (Prüfungen)",
      "Betriebssicherheitsverordnung (BetrSichV), § 14",
      "DIN EN 50699 (VDE 0702), Wiederholungsprüfung",
      "DIN EN 50678 (VDE 0701), Prüfung nach Instandsetzung",
    ],
    folderWords: ["elektrische geräte", "prüfung arbeitsmittel", "prüfprotokoll", "anlagen und geräte"],
    sections: [
      {
        title: "Auftrag",
        fields: [
          f.text("auftraggeber", "Auftraggeber / Betreiber", { prefill: "customer", required: true }),
          f.text("standort", "Standort der Geräte", { prefill: "address" }),
          f.choice("anlass", "Prüfanlass", ["Wiederholungsprüfung", "Prüfung nach Instandsetzung", "Erstprüfung vor Inbetriebnahme"], { required: true }),
          ...PRUEFER,
        ],
      },
      { title: "Messgeräte", fields: [MESSGERAETE] },
      {
        title: "Geräte",
        fields: [
          f.info(
            "grenzwerte",
            "Übliche Grenzwerte",
            "Schutzleiterwiderstand bis 5 m Leitung höchstens 0,3 Ω, je weitere 7,5 m plus 0,1 Ω, höchstens 1 Ω. Isolationswiderstand Schutzklasse I mindestens 1 MΩ, Schutzklasse II mindestens 2 MΩ. Schutzleiterstrom höchstens 3,5 mA, Berührungsstrom höchstens 0,5 mA. Abweichende Herstellerangaben beachten.",
          ),
          f.table(
            "geraete",
            "Geprüfte Geräte",
            [
              col("inv", "Inv.-Nr.", "text", { width: 0.8 }),
              col("bezeichnung", "Gerät", "text", { width: 1.8 }),
              col("typ", "Hersteller / Typ", "text", { width: 1.4 }),
              col("sk", "SK", "choice", { options: ["I", "II", "III"], width: 0.5 }),
              col("sicht", "Sicht", "check", { width: 0.8 }),
              col("rpe", "R PE", "number", { unit: "Ω", limit: { label: "≤ 0,3 Ω", max: 1 } }),
              col("riso", "R Iso", "number", { unit: "MΩ", limit: { label: "≥ 1 MΩ", min: 1 } }),
              col("ipe", "I PE", "number", { unit: "mA", limit: { label: "≤ 3,5 mA", max: 3.5 } }),
              col("ib", "I B", "number", { unit: "mA", limit: { label: "≤ 0,5 mA", max: 0.5 } }),
              col("funktion", "Funktion", "check", { width: 0.8 }),
              col("ok", "Ergebnis", "check", { width: 0.9 }),
            ],
            5,
          ),
        ],
      },
      {
        title: "Ergebnis",
        fields: [
          f.date("naechste", "Nächste Prüfung spätestens", {
            hint: "Die Frist legt der Betreiber in seiner Gefährdungsbeurteilung fest. Richtwert nach DGUV Vorschrift 3: 6 Monate, auf Baustellen 3 Monate; bei geringer Fehlerquote in Werkstätten bis 1 Jahr, in Büros bis 2 Jahre.",
          }),
          f.area("bemerkung", "Bemerkungen", { hint: "Geräte, die die Prüfung nicht bestanden haben, sind zu kennzeichnen und der Benutzung zu entziehen." }),
          f.photos(),
        ],
      },
      { title: "Unterschriften", fields: [UNTERSCHRIFT_PRUEFER, UNTERSCHRIFT_BETREIBER] },
    ],
  },
  {
    id: "elektro-pv",
    name: "Prüfprotokoll Photovoltaikanlage",
    category: "pruefung",
    description: "Prüfung der DC- und AC-Seite mit Messwerten je String: Polarität, Leerlaufspannung, Kurzschlussstrom, Isolation.",
    basis: ["DIN EN 62446-1 (VDE 0126-23-1)", "DIN VDE 0100-712 (PV-Anlagen)", "DIN VDE 0100-600 (Erstprüfung)", "DGUV Vorschrift 3, § 5"],
    folderWords: ["prüfprotokoll", "messungen", "prüfung"],
    sections: [
      {
        title: "Anlage",
        fields: [
          f.text("auftraggeber", "Betreiber", { prefill: "customer", required: true }),
          f.text("anlagenort", "Anschrift der Anlage", { prefill: "address", required: true }),
          f.text("leistung", "Anlagenleistung", { hint: "z. B. 9,8 kWp" }),
          f.text("module", "Modulhersteller / Typ"),
          f.text("wechselrichter", "Wechselrichter Hersteller / Typ"),
          f.text("speicher", "Batteriespeicher (falls vorhanden)"),
          f.choice("anlass", "Prüfanlass", ["Erstprüfung / Inbetriebnahme", "Wiederholungsprüfung", "Prüfung nach Änderung oder Instandsetzung"], { required: true }),
          ...PRUEFER,
        ],
      },
      { title: "Messgeräte", fields: [MESSGERAETE] },
      {
        title: "Besichtigen",
        fields: [
          f.check("b_module", "Module, Befestigung und Unterkonstruktion"),
          f.check("b_dc", "DC-Leitungen und Steckverbinder (UV-beständig, geschützt verlegt)"),
          f.check("b_gak", "Generatoranschlusskasten / DC-Trennstelle"),
          f.check("b_uesp", "Überspannungsschutz"),
          f.check("b_erdung", "Erdung und Potentialausgleich des Gestells"),
          f.check("b_kennzeichnung", "Kennzeichnung und Warnhinweise (PV-Anlage, Trennstellen)"),
          f.check("b_unterlagen", "Datenblätter, Stringplan und Schaltplan vorhanden"),
        ],
      },
      {
        title: "Messwerte DC-Seite",
        fields: [
          f.table(
            "strings",
            "Messwerte je String",
            [
              col("string", "String", "text", { width: 0.7 }),
              col("anzahl", "Module", "number", { width: 0.7 }),
              col("polaritaet", "Polarität", "check", { width: 0.9 }),
              col("uoc_soll", "Uoc soll", "number", { unit: "V" }),
              col("uoc", "Uoc ist", "number", { unit: "V" }),
              col("isc_soll", "Isc soll", "number", { unit: "A" }),
              col("isc", "Isc ist", "number", { unit: "A" }),
              col("riso_p", "R Iso +", "number", { unit: "MΩ", limit: { label: "≥ 1 MΩ", min: 1 } }),
              col("riso_m", "R Iso −", "number", { unit: "MΩ", limit: { label: "≥ 1 MΩ", min: 1 } }),
              col("ok", "Ergebnis", "check", { width: 0.9 }),
            ],
            2,
            {
              hint: "Sollwerte aus Datenblatt mal Anzahl Module, temperatur- und einstrahlungsabhängig. Vor der Isolationsmessung den Wechselrichter trennen; Prüfspannung nach Systemspannung wählen.",
            },
          ),
          f.text("einstrahlung", "Einstrahlung / Modultemperatur bei Messung", { hint: "z. B. 820 W/m², 38 °C" }),
        ],
      },
      {
        title: "AC-Seite und Funktion",
        fields: [
          f.num("ac_rpe", "Schutzleiter Wechselrichter bis Haupterdungsschiene", "Ω"),
          f.num("ac_zs", "Schleifenimpedanz Zs am Wechselrichter-Abgang", "Ω", { hint: "Mit dem Höchstwert für das vorgeschaltete Schutzorgan vergleichen." }),
          f.num("ac_ta", "Auslösezeit RCD bei IΔn", "ms"),
          f.check("f_einspeisung", "Wechselrichter startet und speist ein"),
          f.check("f_netzausfall", "Abschaltung bei Netzausfall (NA-Schutz)"),
          f.check("f_kommunikation", "Überwachung / Kommunikation"),
        ],
      },
      { title: "Mängel", fields: [MAENGEL] },
      { title: "Ergebnis", fields: [ERGEBNIS_ANLAGE, f.date("naechste", "Nächste Prüfung spätestens"), f.area("bemerkung", "Bemerkungen"), f.photos()] },
      { title: "Unterschriften", fields: [UNTERSCHRIFT_PRUEFER, UNTERSCHRIFT_BETREIBER] },
    ],
  },
  {
    id: "elektro-ladepunkt",
    name: "Prüfprotokoll Ladeeinrichtung (Wallbox / Ladesäule)",
    category: "pruefung",
    description: "Prüfung von Ladepunkten für Elektrofahrzeuge mit Messwerten und Funktionsprüfung über Prüfadapter.",
    basis: ["DIN VDE 0100-722 (Stromversorgung von Elektrofahrzeugen)", "DIN VDE 0100-600 (Erstprüfung)", "DIN EN 61851-1 (VDE 0122-1)", "DGUV Vorschrift 3, § 5"],
    folderWords: ["prüfprotokoll", "messungen", "prüfung"],
    sections: [
      {
        title: "Ladeeinrichtung",
        fields: [
          f.text("auftraggeber", "Betreiber", { prefill: "customer", required: true }),
          f.text("anlagenort", "Standort", { prefill: "address", required: true }),
          f.text("typ", "Hersteller / Typ", { required: true }),
          f.text("seriennr", "Seriennummer"),
          f.text("ladeleistung", "Ladeleistung / Anzahl Ladepunkte", { hint: "z. B. 11 kW, 1 Ladepunkt" }),
          f.choice("netzform", "Netzform", ["TN-S", "TN-C-S", "TT"]),
          f.text("schutzorgan", "Vorgeschaltetes Schutzorgan", { hint: "z. B. B16, 3-polig" }),
          f.choice("rcd_typ", "Fehlerstromschutz", ["Typ A mit 6-mA-DC-Fehlerstromerkennung (RDC-DD)", "Typ B", "Typ EV / A-EV", "Typ F"]),
          f.choice("anlass", "Prüfanlass", ["Erstprüfung / Inbetriebnahme", "Wiederholungsprüfung", "Prüfung nach Änderung oder Instandsetzung"], { required: true }),
          ...PRUEFER,
        ],
      },
      { title: "Messgeräte", fields: [MESSGERAETE] },
      {
        title: "Besichtigen",
        fields: [
          f.check("b_gehaeuse", "Gehäuse, Ladekabel und Steckvorrichtung unbeschädigt"),
          f.check("b_typenschild", "Typenschild und Kennzeichnung"),
          f.check("b_schutzart", "Schutzart und Anfahrschutz passend zum Standort"),
          f.check("b_leitung", "Zuleitung und Absicherung passend dimensioniert"),
          f.check("b_anmeldung", "Anmeldung / Genehmigung beim Netzbetreiber vorhanden"),
        ],
      },
      {
        title: "Messen",
        fields: [
          f.num("rpe", "Schutzleiterwiderstand bis Haupterdungsschiene", "Ω"),
          f.num("riso", "Isolationswiderstand", "MΩ", { limit: { label: "≥ 1 MΩ", min: 1 }, hint: "Ladeelektronik nach Herstellerangabe trennen." }),
          f.num("zs", "Schleifenimpedanz Zs", "Ω", { hint: "Mit dem Höchstwert für das Schutzorgan vergleichen." }),
          f.num("idn", "Bemessungsfehlerstrom IΔn", "mA"),
          f.num("ta", "Auslösezeit bei IΔn", "ms"),
          f.text("spannung", "Spannung L-N / L-L", { hint: "z. B. 231 V / 400 V" }),
        ],
      },
      {
        title: "Funktion mit Prüfadapter",
        fields: [
          f.check("f_cp", "Fahrzeugzustände A bis C über Prüfadapter (CP-Signal)"),
          f.check("f_laden", "Ladefreigabe und Ladevorgang"),
          f.check("f_pe", "Abschaltung bei Schutzleiterfehler"),
          f.check("f_dc", "Abschaltung bei DC-Fehlerstrom (6 mA)"),
          f.check("f_drehfeld", "Drehfeld"),
        ],
      },
      { title: "Mängel", fields: [MAENGEL] },
      { title: "Ergebnis", fields: [ERGEBNIS_ANLAGE, f.date("naechste", "Nächste Prüfung spätestens"), f.area("bemerkung", "Bemerkungen"), f.photos()] },
      { title: "Unterschriften", fields: [UNTERSCHRIFT_PRUEFER, UNTERSCHRIFT_BETREIBER] },
    ],
  },
  {
    id: "thermografie",
    name: "Thermografie-Bericht elektrische Anlage",
    category: "pruefung",
    description: "Wärmebildprüfung von Verteilungen, PV-Anlagen und Ladepunkten mit Befunden, Bewertung und Maßnahmen.",
    basis: ["DIN 54191 (Thermografische Prüfung elektrischer Anlagen)", "VdS 2858 (Thermografie in elektrischen Anlagen)", "DIN EN ISO 18434-1 (Zustandsüberwachung, Thermografie)"],
    folderWords: ["thermografie", "prüfprotokoll", "prüfung"],
    sections: [
      {
        title: "Objekt",
        fields: [
          f.text("auftraggeber", "Auftraggeber / Betreiber", { prefill: "customer", required: true }),
          f.text("anlagenort", "Anschrift", { prefill: "address", required: true }),
          f.text("umfang", "Prüfumfang", { required: true, hint: "z. B. Hauptverteilung, 4 Unterverteilungen" }),
          ...PRUEFER.slice(0, 2),
          f.text("kamera", "Wärmebildkamera / Seriennummer"),
        ],
      },
      {
        title: "Messbedingungen",
        fields: [
          f.num("umgebung", "Umgebungstemperatur", "°C"),
          f.num("last", "Belastung während der Messung", "% Nennlast", { hint: "Für aussagekräftige Bilder möglichst hohe Last, Richtwert mindestens 40 %." }),
          f.text("emission", "Eingestellter Emissionsgrad"),
          f.text("abstand", "Messabstand / Optik"),
        ],
      },
      {
        title: "Befunde",
        fields: [
          f.table(
            "befunde",
            "Befunde",
            [
              col("nr", "Bild-Nr.", "text", { width: 0.7 }),
              col("ort", "Ort / Bauteil", "text", { width: 2 }),
              col("tmax", "T max", "number", { unit: "°C" }),
              col("tref", "T Ref.", "number", { unit: "°C" }),
              col("dt", "ΔT", "number", { unit: "K" }),
              col("bewertung", "Bewertung", "choice", { options: ["unauffällig", "beobachten", "zeitnah beheben", "sofort beheben"], width: 1.2 }),
              col("massnahme", "Maßnahme", "text", { width: 2 }),
            ],
            3,
            { hint: "Die Bewertung richtet sich nach der Temperaturdifferenz zu vergleichbaren Bauteilen und nach den zulässigen Temperaturen der Betriebsmittel." },
          ),
        ],
      },
      { title: "Ergebnis", fields: [f.area("ergebnis_text", "Zusammenfassung", { required: true }), f.date("naechste", "Empfohlene nächste Thermografie"), f.photos("fotos", "Wärmebilder und Fotos")] },
      { title: "Unterschriften", fields: [f.sign("sig_pruefer", "Thermograf", { required: true }), UNTERSCHRIFT_BETREIBER] },
    ],
  },
  {
    id: "unterweisung",
    name: "Unterweisung im Arbeitsschutz",
    category: "arbeitsschutz",
    description: "Nachweis über Erst- und Wiederholungsunterweisungen mit Inhalten und Unterschrift jeder teilnehmenden Person.",
    basis: ["Arbeitsschutzgesetz (ArbSchG), § 12", "DGUV Vorschrift 1, § 4 (Unterweisung, mindestens einmal jährlich, dokumentiert)", "Je nach Thema z. B. BetrSichV § 12, GefStoffV § 14"],
    folderWords: ["unterweisung", "unterschriftenliste", "arbeitsschutz"],
    sections: [
      {
        title: "Unterweisung",
        fields: [
          f.text("thema", "Thema", { required: true, hint: "z. B. Jährliche Unterweisung Elektrische Gefährdungen" }),
          f.choice(
            "anlass",
            "Anlass",
            ["Erstunterweisung vor Aufnahme der Tätigkeit", "Wiederholungsunterweisung (mindestens jährlich)", "Neue Arbeitsmittel, Verfahren oder Arbeitsplätze", "Nach einem Unfall oder Beinaheunfall", "Sonstiger Anlass"],
            { required: true },
          ),
          f.date("datum", "Datum", { prefill: "today", required: true }),
          f.text("dauer", "Dauer", { hint: "z. B. 45 Minuten" }),
          f.text("ort", "Ort", { prefill: "address" }),
          f.text("unterweisender", "Unterwiesen durch", { prefill: "user", required: true }),
        ],
      },
      {
        title: "Inhalte",
        fields: [
          f.multi("themen", "Behandelte Inhalte", [
            "Gefährdungen am Arbeitsplatz und Schutzmaßnahmen",
            "Betriebsanweisungen",
            "Persönliche Schutzausrüstung",
            "Elektrische Gefährdungen und die fünf Sicherheitsregeln",
            "Leitern, Tritte und Gerüste",
            "Maschinen und Elektrowerkzeuge",
            "Gefahrstoffe",
            "Verkehrswege und Baustellenordnung",
            "Erste Hilfe und Verhalten bei Unfällen",
            "Brandschutz, Flucht- und Rettungswege",
          ]),
          f.area("inhalte", "Weitere Inhalte und ausgegebene Unterlagen"),
          f.choice("verstaendnis", "Verständnis geprüft durch", ["Gespräch und Rückfragen", "Verständnisfragen / Test", "Praktische Übung"]),
        ],
      },
      {
        title: "Teilnehmende",
        intro: "Jede Person unterschreibt selbst, am besten direkt auf dem Handy oder Tablet.",
        fields: [
          f.table(
            "teilnehmer",
            "Teilnehmende",
            [col("name", "Name", "text", { width: 1.6 }), col("taetigkeit", "Tätigkeit / Firma", "text", { width: 1.4 }), col("unterschrift", "Unterschrift", "signature", { width: 1.6 })],
            3,
            { confirm: "Mit meiner Unterschrift bestätige ich, dass ich an der Unterweisung teilgenommen und die Inhalte verstanden habe." },
          ),
        ],
      },
      { title: "Unterschrift", fields: [f.sign("sig_unterweisender", "Unterweisende Person", { required: true })] },
    ],
  },
  {
    id: "belehrung",
    name: "Belehrung mit Kenntnisnahme",
    category: "arbeitsschutz",
    description: "Für Belehrungen einzelner Personen, z. B. Datenschutz und Verschwiegenheit, Hygiene oder Betriebsordnung.",
    basis: ["Je nach Thema, z. B. DSGVO Art. 29 und 32 (Vertraulichkeit), § 43 Abs. 4 IfSG (Wiederholungsbelehrung), Betriebsvereinbarungen"],
    folderWords: ["belehrung", "unterweisung", "arbeitsschutz"],
    sections: [
      {
        title: "Belehrung",
        fields: [
          f.choice("art", "Art der Belehrung", ["Datenschutz und Vertraulichkeit", "Hygienebelehrung", "Betriebs- und Hausordnung", "Sonstige Belehrung"], { required: true }),
          f.text("thema", "Thema", { required: true }),
          f.area("inhalt", "Inhalt der Belehrung", { required: true, hint: "Kurz und verständlich, was die Person wissen und beachten muss." }),
          f.date("datum", "Datum", { prefill: "today", required: true }),
          f.text("belehrender", "Belehrt durch", { prefill: "user", required: true }),
        ],
      },
      {
        title: "Belehrte Person",
        fields: [
          f.text("person", "Name", { required: true }),
          f.text("taetigkeit", "Tätigkeit / Abteilung"),
          f.sign("sig_person", "Belehrte Person", {
            required: true,
            confirm: "Ich wurde über die genannten Inhalte belehrt, habe sie verstanden und kann die Unterlagen jederzeit einsehen.",
          }),
          f.sign("sig_belehrender", "Belehrende Person", { required: true }),
        ],
      },
    ],
  },
  {
    id: "gefaehrdungsbeurteilung",
    name: "Gefährdungsbeurteilung",
    category: "arbeitsschutz",
    description: "Gefährdungen einer Tätigkeit oder eines Arbeitsplatzes erfassen, Maßnahmen festlegen und Wirksamkeit nachhalten.",
    basis: ["Arbeitsschutzgesetz (ArbSchG), §§ 5 und 6", "DGUV Vorschrift 1, § 3", "Für Arbeitsmittel: BetrSichV § 3, TRBS 1111"],
    folderWords: ["gefährdungsbeurteilung", "arbeitsschutz"],
    sections: [
      {
        title: "Arbeitsbereich",
        fields: [
          f.text("betrieb", "Betrieb", { prefill: "company", required: true }),
          f.text("bereich", "Arbeitsbereich / Tätigkeit", { required: true, hint: "z. B. Montage auf Baustellen, Arbeiten an Verteilungen" }),
          f.text("ort", "Ort", { prefill: "address" }),
          f.text("ersteller", "Erstellt von", { prefill: "user", required: true }),
          f.text("beteiligte", "Beteiligt", { hint: "z. B. Fachkraft für Arbeitssicherheit, Betriebsarzt, Beschäftigte" }),
          f.date("datum", "Datum", { prefill: "today", required: true }),
        ],
      },
      {
        title: "Gefährdungen und Maßnahmen",
        intro: "Maßnahmen in dieser Reihenfolge prüfen: Gefahr vermeiden, technische, organisatorische, zuletzt persönliche Schutzmaßnahmen.",
        fields: [
          f.table(
            "gefaehrdungen",
            "Gefährdungen",
            [
              col("gefaehrdung", "Gefährdung", "text", { width: 2 }),
              col("risiko", "Risiko", "choice", { options: ["gering", "mittel", "hoch"], width: 0.8 }),
              col("massnahme", "Maßnahme", "text", { width: 2.4 }),
              col("verantwortlich", "Wer", "text", { width: 1 }),
              col("frist", "Bis", "date", { width: 0.9 }),
              col("wirksam", "Wirksam", "check", { width: 0.9 }),
            ],
            4,
          ),
        ],
      },
      {
        title: "Überprüfung",
        fields: [
          f.date("naechste", "Nächste Überprüfung", { hint: "Bei Änderungen, nach Unfällen und in regelmäßigen Abständen erneut prüfen." }),
          f.area("bemerkung", "Bemerkungen"),
          f.photos(),
        ],
      },
      { title: "Unterschriften", fields: [f.sign("sig_ersteller", "Erstellt", { required: true }), f.sign("sig_unternehmer", "Unternehmer / Verantwortlicher")] },
    ],
  },
  {
    id: "leitern",
    name: "Prüfung Leitern und Tritte",
    category: "arbeitsschutz",
    description: "Regelmäßige Sichtprüfung von Leitern und Tritten durch eine zur Prüfung befähigte Person.",
    basis: ["Betriebssicherheitsverordnung (BetrSichV), § 14", "TRBS 2121 Teil 2 (Leitern)", "DGUV Information 208-016"],
    folderWords: ["leitern", "prüfung arbeitsmittel", "arbeitsschutz"],
    sections: [
      {
        title: "Prüfung",
        fields: [
          f.date("pruefdatum", "Datum der Prüfung", { prefill: "today", required: true }),
          f.text("pruefer", "Zur Prüfung befähigte Person", { prefill: "user", required: true }),
          f.text("standort", "Standort", { prefill: "address" }),
        ],
      },
      {
        title: "Leitern und Tritte",
        fields: [
          f.table(
            "leitern",
            "Geprüfte Leitern",
            [
              col("nr", "Nr.", "text", { width: 0.6 }),
              col("art", "Art", "choice", { options: ["Anlegeleiter", "Stehleiter", "Mehrzweckleiter", "Podestleiter", "Tritt"], width: 1.2 }),
              col("standort", "Standort", "text", { width: 1.2 }),
              col("holme", "Holme / Sprossen", "check"),
              col("gelenke", "Gelenke / Spreizsicherung", "check"),
              col("fuesse", "Füße", "check"),
              col("kennzeichnung", "Kennzeichnung", "check"),
              col("ok", "Ergebnis", "check"),
            ],
            4,
            { hint: "Beschädigte Leitern sofort kennzeichnen und der Benutzung entziehen." },
          ),
        ],
      },
      { title: "Ergebnis", fields: [f.date("naechste", "Nächste Prüfung"), f.area("bemerkung", "Bemerkungen"), f.photos()] },
      { title: "Unterschrift", fields: [f.sign("sig_pruefer", "Prüfer", { required: true })] },
    ],
  },
  {
    id: "abnahme",
    name: "Abnahmeprotokoll",
    category: "abnahme",
    description: "Abnahme einer Werkleistung mit Mängeln, Vorbehalten, übergebenen Unterlagen und Unterschrift beider Seiten.",
    basis: ["BGB § 640 (Abnahme)", "BGB §§ 634, 634a (Mängelrechte, Verjährung)", "VOB/B § 12, wenn vereinbart"],
    folderWords: ["abnahme", "übergabe"],
    sections: [
      {
        title: "Beteiligte und Leistung",
        fields: [
          f.text("auftraggeber", "Auftraggeber", { prefill: "customer", required: true }),
          f.text("auftragnehmer", "Auftragnehmer", { prefill: "company", required: true }),
          f.text("bauvorhaben", "Bauvorhaben / Ort", { prefill: "address", required: true }),
          f.area("leistung", "Abgenommene Leistung", { prefill: "project", required: true }),
          f.text("vertrag", "Grundlage", { hint: "z. B. Auftrag vom 12.03.2026, Angebot Nr. 2026-041" }),
          f.date("datum", "Datum der Abnahme", { prefill: "today", required: true }),
          f.text("anwesend", "Anwesend"),
        ],
      },
      {
        title: "Ergebnis der Abnahme",
        fields: [
          f.choice(
            "abnahme",
            "Ergebnis",
            [
              "Die Leistung wird ohne Mängel abgenommen.",
              "Die Leistung wird abgenommen. Die unten genannten Mängel werden vorbehalten und vom Auftragnehmer beseitigt.",
              "Die Abnahme wird wegen wesentlicher Mängel verweigert.",
            ],
            { required: true },
          ),
          f.table(
            "maengel",
            "Mängel und offene Restarbeiten",
            [col("beschreibung", "Beschreibung", "text", { width: 3 }), col("ort", "Ort", "text", { width: 1.2 }), col("frist", "Frist", "date", { width: 0.9 })],
            1,
          ),
          f.choice("vertragsstrafe", "Vertragsstrafe (nur wenn vereinbart)", ["Nicht vereinbart", "Wird vorbehalten", "Wird nicht vorbehalten"]),
          f.multi("unterlagen", "Übergebene Unterlagen", ["Bedienungs- und Wartungsanleitungen", "Prüfprotokolle", "Pläne und Schaltpläne", "Garantie- und Herstellerunterlagen", "Schlüssel / Zugangsdaten"]),
          f.info(
            "hinweis",
            "Gut zu wissen",
            "Mit der Abnahme werden in der Regel die Vergütung fällig und die Verjährungsfrist für Mängelansprüche beginnt. Bekannte Mängel muss der Auftraggeber bei der Abnahme vorbehalten, sonst können Rechte entfallen (§ 640 Abs. 3 BGB). Eine vereinbarte Vertragsstrafe muss ebenfalls bei der Abnahme vorbehalten werden.",
          ),
          f.area("bemerkung", "Bemerkungen"),
          f.photos(),
        ],
      },
      {
        title: "Unterschriften",
        fields: [f.sign("sig_auftraggeber", "Auftraggeber", { required: true }), f.sign("sig_auftragnehmer", "Auftragnehmer", { required: true })],
      },
    ],
  },
  {
    id: "wartung",
    name: "Wartungsprotokoll",
    category: "abnahme",
    description: "Ausgeführte Wartungsarbeiten, Material, Zählerstände und festgestellte Mängel mit Unterschrift des Kunden.",
    basis: ["Herstellervorgaben und Wartungsvertrag", "Für Arbeitsmittel: BetrSichV § 10 (Instandhaltung)"],
    folderWords: ["wartung", "prüfprotokoll"],
    sections: [
      {
        title: "Anlage",
        fields: [
          f.text("auftraggeber", "Kunde", { prefill: "customer", required: true }),
          f.text("anlagenort", "Standort", { prefill: "address", required: true }),
          f.text("anlage", "Anlage / Gerät", { required: true }),
          f.text("typ", "Hersteller / Typ / Seriennummer"),
          f.date("datum", "Datum", { prefill: "today", required: true }),
          f.text("techniker", "Techniker", { prefill: "user", required: true }),
          f.text("stand", "Zählerstand / Betriebsstunden"),
        ],
      },
      {
        title: "Arbeiten",
        fields: [
          f.table(
            "arbeiten",
            "Ausgeführte Arbeiten",
            [col("taetigkeit", "Tätigkeit", "text", { width: 3 }), col("ergebnis", "Ergebnis", "check", { width: 0.9 }), col("bemerkung", "Bemerkung", "text", { width: 2 })],
            4,
          ),
          f.table("material", "Verwendetes Material", [col("material", "Material", "text", { width: 3 }), col("menge", "Menge", "text")], 1),
        ],
      },
      {
        title: "Ergebnis",
        fields: [
          f.choice("ergebnis", "Zustand nach der Wartung", ["Anlage in Ordnung und betriebsbereit", "Betriebsbereit, Mängel siehe unten", "Nicht betriebsbereit, Mängel siehe unten"], { required: true }),
          f.area("maengel", "Festgestellte Mängel und Empfehlungen"),
          f.date("naechste", "Nächste Wartung"),
          f.photos(),
        ],
      },
      { title: "Unterschriften", fields: [f.sign("sig_techniker", "Techniker", { required: true }), f.sign("sig_kunde", "Kunde")] },
    ],
  },
];

export function protocolTemplate(id: string): ProtocolTemplate | undefined {
  return PROTOCOL_TEMPLATES.find((t) => t.id === id);
}

/* ---------- Werte prüfen und bereinigen ---------- */

const MAX_TEXT = 4000;
const MAX_ROWS = 300;
const MAX_PHOTOS = 40;
/** Eine Unterschrift als PNG; großzügig, aber begrenzt. */
export const MAX_SIGNATURE_CHARS = 300_000;
const SIGNATURE_PREFIX = "data:image/png;base64,";
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const str = (v: unknown, max = MAX_TEXT) => (typeof v === "string" ? v.slice(0, max) : "");

function cleanSignature(v: unknown): Signature | null {
  if (!v || typeof v !== "object") return null;
  const s = v as Record<string, unknown>;
  const image = str(s.image, MAX_SIGNATURE_CHARS + 1);
  if (!image.startsWith(SIGNATURE_PREFIX) || image.length > MAX_SIGNATURE_CHARS || !/^[A-Za-z0-9+/=]+$/.test(image.slice(SIGNATURE_PREFIX.length))) return null;
  const signedAt = str(s.signedAt, 40);
  return { image, name: str(s.name, 120).trim(), signedAt: Number.isNaN(Date.parse(signedAt)) ? new Date().toISOString() : signedAt };
}

function cleanCell(type: ColumnType, v: unknown, options?: string[]): unknown {
  switch (type) {
    case "signature":
      return cleanSignature(v);
    case "check":
      return v === "ok" || v === "mangel" || v === "entfaellt" ? v : "";
    case "choice":
      return typeof v === "string" && (options ?? []).includes(v) ? v : "";
    case "date":
      return typeof v === "string" && DATE.test(v) ? v : "";
    default:
      return str(v, 500);
  }
}

/**
 * Behält nur Felder, die die Vorlage kennt, im passenden Format. Unbekanntes fällt still weg, damit weder
 * alte Entwürfe noch manipulierte Anfragen Unsinn ins PDF bringen.
 */
export function cleanProtocolData(template: ProtocolTemplate, raw: unknown): ProtocolData {
  const input = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const out: ProtocolData = {};
  for (const field of template.sections.flatMap((s) => s.fields)) {
    const v = input[field.key];
    switch (field.type) {
      case "info":
        break;
      case "text":
      case "textarea":
      case "number":
        out[field.key] = str(v);
        break;
      case "date":
        out[field.key] = typeof v === "string" && DATE.test(v) ? v : "";
        break;
      case "choice":
        out[field.key] = typeof v === "string" && field.options?.includes(v) ? v : "";
        break;
      case "multi":
        out[field.key] = Array.isArray(v) ? field.options!.filter((o) => v.includes(o)) : [];
        break;
      case "check":
        out[field.key] = cleanCell("check", v) as string;
        break;
      case "signature":
        out[field.key] = cleanSignature(v);
        break;
      case "photos":
        out[field.key] = Array.isArray(v)
          ? v
              .filter((p): p is { fileId: string; caption?: unknown } => !!p && typeof p === "object" && UUID.test(String((p as { fileId?: unknown }).fileId)))
              .slice(0, MAX_PHOTOS)
              .map((p) => ({ fileId: p.fileId, caption: str(p.caption, 200) }))
          : [];
        break;
      case "table":
        out[field.key] = Array.isArray(v)
          ? v.slice(0, MAX_ROWS).map((row) => {
              const r = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
              return Object.fromEntries(field.columns!.map((c) => [c.key, cleanCell(c.type, r[c.key], c.options)]));
            })
          : [];
        break;
    }
  }
  return out;
}

export interface PrefillContext {
  company: string;
  user: string;
  today: string;
  customer: string | null;
  address: string | null;
  project: string;
}

/** Neuer Entwurf: Startwerte, Vorbelegung und leere Tabellenzeilen. */
export function initialProtocolData(template: ProtocolTemplate, ctx: PrefillContext): ProtocolData {
  const data: Record<string, unknown> = {};
  for (const field of template.sections.flatMap((s) => s.fields)) {
    if (field.prefill) data[field.key] = ctx[field.prefill] ?? "";
    else if (field.initial) data[field.key] = field.initial;
    if (field.type === "table") data[field.key] = Array.from({ length: field.rows ?? 1 }, () => ({}));
  }
  return cleanProtocolData(template, data);
}

const isEmpty = (v: unknown) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);

/** Ist eine Tabellenzeile leer? Leere Zeilen erscheinen nicht im PDF. */
export function rowIsEmpty(row: Record<string, unknown>): boolean {
  return Object.values(row).every(isEmpty);
}

/** Was vor dem Abschließen noch fehlt, in Worten für die Person, die ausfüllt. */
export function missingForCompletion(template: ProtocolTemplate, data: ProtocolData): string[] {
  const missing: string[] = [];
  for (const section of template.sections) {
    for (const field of section.fields) {
      if (field.required && isEmpty(data[field.key])) {
        missing.push(field.type === "signature" ? `Unterschrift „${field.label}“` : `„${field.label}“ (${section.title})`);
      }
    }
  }
  return missing;
}

/** Wert mit Komma oder Punkt als Zahl lesen; leer oder Text ergibt null. */
export function parseNumber(v: unknown): number | null {
  if (typeof v !== "string") return null;
  const t = v.trim().replace(/\s/g, "").replace(",", ".");
  if (!/^[-+]?\d*\.?\d+$/.test(t)) return null;
  return Number(t);
}

/** Liegt ein Messwert eindeutig außerhalb des Grenzwerts? Nur ein Hinweis, die Bewertung trifft der Prüfer. */
export function outsideLimit(limit: Limit | undefined, v: unknown): boolean {
  const n = parseNumber(v);
  if (!limit || n === null) return false;
  return (limit.min !== undefined && n < limit.min) || (limit.max !== undefined && n > limit.max);
}

export const protocolCreateSchema = z.object({
  templateId: z.string().max(80).optional(),
  /** Abgeschlossenes oder offenes Protokoll als Grundlage, z. B. für die Wiederholungsprüfung. */
  copyFrom: z.uuid().optional(),
});

export const protocolUpdateSchema = z.object({
  title: z.string().trim().min(1, "Bitte geben Sie dem Protokoll einen Namen.").max(160, "Bitte höchstens 160 Zeichen.").optional(),
  data: z.record(z.string().max(60), z.unknown()).optional(),
});

export const protocolCompleteSchema = z.object({
  folderId: z.uuid().optional(),
});

/* ---------- Firmenkopf und Weitergabe an Kunden ---------- */

/** Logo für Protokolle: PNG oder JPG, höchstens so groß. */
export const LOGO_MAX_BYTES = 1024 * 1024;
export const SHARE_DAYS = [7, 14, 30, 90] as const;

/** Wie die Firma oben auf jedem Protokoll steht. */
export interface CompanyBranding {
  /** Name im Protokoll; ohne eigene Angabe der Firmenname aus der Registrierung. */
  name: string;
  /** Eigene Angabe, leer = Firmenname aus der Registrierung. */
  customName: string;
  companyName: string;
  /** Zusatzzeile, z. B. Anschrift und Telefon. */
  line: string;
  hasLogo: boolean;
  /** Ändert sich mit jedem neuen Logo; für die Bildadresse. */
  logoVersion: string | null;
  canEdit: boolean;
}

export interface ProtocolShareInfo {
  id: string;
  email: string;
  protocols: { id: string; title: string }[];
  createdBy: string | null;
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
  lastOpenedAt: string | null;
  downloads: number;
}

/** Was der Kunde über den Link sieht. */
export interface PublicShare {
  company: string;
  line: string;
  hasLogo: boolean;
  sender: string | null;
  message: string | null;
  expiresAt: string;
  protocols: { id: string; title: string; templateName: string; completedAt: string | null; available: boolean }[];
}

export const brandingUpdateSchema = z.object({
  name: z.string().trim().max(120, "Bitte höchstens 120 Zeichen für den Namen."),
  line: z.string().trim().max(200, "Bitte höchstens 200 Zeichen für die Zusatzzeile."),
});

export const protocolShareSchema = z.object({
  protocolIds: z.array(z.uuid()).min(1, "Bitte wählen Sie mindestens ein abgeschlossenes Protokoll.").max(50, "Bitte höchstens 50 Protokolle auf einmal."),
  email: z.string().max(254),
  message: z.string().trim().max(1000, "Bitte höchstens 1000 Zeichen für die Nachricht.").optional(),
  days: z
    .number()
    .int()
    .refine((d) => (SHARE_DAYS as readonly number[]).includes(d), "Bitte wählen Sie, wie lange der Link gültig ist.")
    .default(30),
});
